/**
 * licence-banner — the notice the admin shell stands above every admin page
 * while the licence is in grace or read-only. Not dismissible: it states the
 * deployment's sales, which no member can change from here. Grace leads with
 * the date sales stop; read-only says what keeps working.
 *
 * The existing notice anatomy: grace is the wallet wizard's warning notice,
 * read-only the same on the error surface.
 */
import { formatDate } from "@mhmo91/rx";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { css, html } from "lit";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../i18n/localize.ts";
import { adminLicence } from "./licence.state.ts";

@schmancyElement("licence-banner")
export class LicenceBanner extends LocalizeMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: block;
			}
		`,
	];

	override render() {
		const licence = adminLicence.get();
		const grace = licence?.state === "grace";
		return when(
			licence !== null && licence.state !== "active",
			() => html`
				<schmancy-surface
					type=${grace ? "warning" : "error"}
					role="status"
					class="mx-4 mt-3 flex items-start gap-3 rounded-xl p-4"
				>
					<schmancy-icon class=${this.classMap({ "shrink-0": true, "text-warning-on-container": grace, "text-error-on-container": !grace })}>
						${grace ? "info" : "error"}
					</schmancy-icon>
					<div class="flex flex-col gap-1">
						<schmancy-typography type="title" token="sm" class=${this.classMap({ "text-warning-on-container": grace, "text-error-on-container": !grace })}>
							${
								grace
									? this.t("admin.licence.banner.graceTitle", {
											stops:
												formatDate(licence?.salesStopAt, this.locale) ?? "",
										})
									: this.t("admin.licence.banner.readOnlyTitle")
							}
						</schmancy-typography>
						<schmancy-typography type="body" token="sm" class=${this.classMap({ "text-warning-on-container": grace, "text-error-on-container": !grace })}>
							${this.t(grace ? "admin.licence.banner.graceBody" : "admin.licence.banner.readOnlyBody")}
						</schmancy-typography>
					</div>
				</schmancy-surface>
			`,
		);
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"licence-banner": LicenceBanner;
	}
}
